import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Info } from 'lucide-react';
import { AnnouncementRevision } from '../api/lots';
import { useLanguage } from '../context/LanguageContext';
import { tenderVersionCopy } from '../locales/tenderVersions';

export default function TenderRevisionNotice({ revision, compact = false }: {
  revision?: AnnouncementRevision | null;
  compact?: boolean;
}) {
  const { language } = useLanguage();
  const copy = tenderVersionCopy[language];
  if (revision?.is_latest !== false || !revision.latest || revision.latest.number === revision.number) return null;
  const latest = revision.latest;
  const label = revision.complete ? copy.latest : copy.latestKnown;
  // Build the portal route from the announcement number; never navigate to an external API-supplied URL.
  const href = `/lots?anno=${encodeURIComponent(latest.number)}`;
  const status = language === 'kk' ? latest.status?.name_kz || latest.status?.name_ru : latest.status?.name_ru || latest.status?.name_kz;
  const link = <Link to={href} onClick={event => event.stopPropagation()}
    className="procurement-focus inline-flex items-center gap-1 rounded font-bold text-amber-900 underline decoration-amber-400 underline-offset-4 hover:text-amber-700">
    {label}: {latest.number}<ArrowUpRight aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
  </Link>;
  if (compact) return <div className="mt-2 max-w-52 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs leading-relaxed">{link}</div>;
  return <aside className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5" aria-label={copy.title}>
    <div className="flex items-start gap-3">
      <Info aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
      <div className="min-w-0">
        <p className="font-bold text-amber-950">{copy.title}</p>
        <p className="mt-1 text-sm leading-relaxed text-amber-900">{copy.description}</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
          {link}{status && <span className="rounded-lg bg-white/80 px-2 py-1 text-xs font-semibold text-amber-900">{status}</span>}
        </div>
        {!revision.complete && <p className="mt-2 text-xs text-amber-800">{copy.incomplete}</p>}
      </div>
    </div>
  </aside>;
}
